import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/css'
import { connect } from 'react-redux'
import { Route, Redirect, Switch, withRouter } from 'react-router-dom'

import CreateUser from './containers/CreateUser'
import ResumeScene from './scenes/ResumeScene'
import AreaRolesScene from './scenes/AreaRolesScene'
import SkillsScene from './scenes/SkillsScene'
import EmploymentType from './containers/EmploymentType'
import CitySelect from './containers/CitySelect'
import JobSearchStatus from './containers/JobSearchStatus'
import CitizenshipStatusScene from './scenes/CitizenshipStatusScene'
import SalaryExpectations from './containers/SalaryExpectations'
import CellphoneNumber from './containers/CellphoneNumber'
import SocialSignupConfirm from './containers/SocialSignupConfirm'
import WorkExperienceScene from './scenes/WorkExperienceScene'
import WrappingUp from './containers/WrappingUp'
import EducationScene from './scenes/EducationScene'
import Header from './components/Header'
import StandardNavigation from './components/StandardNavigation'
import ProgressBar from './components/ProgressBar'
import { useCaptureUTM } from '../CandidateExperience/hooks'

import { canAccessStep, firstStepUrlInOnboardingPhase } from './helpers/onboardingSteps'
import { onboardingPhase as getOnboardingPhase } from './selectors/currentUser'

import LoadingScene from '../CandidateExperience/scenes/LoadingScene'

const styles = {
  container: css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
  }),
}

const OnboardingMain = ({ onboardingPhase, location: { pathname, search }, history: { replace }, ...props }) => {
  const candidateSlug = props.candidate && props.candidate.slug
  const isLoggedIn = Boolean(candidateSlug)
  const confirmingSignupDetails = onboardingPhase === 'confirm_details'

  useCaptureUTM(search)

  useEffect(() => {
    if (!isLoggedIn) return

    if (confirmingSignupDetails) {
      replace(`/candidate/${candidateSlug}/confirm-details${window.location.search}`)
      return
    }

    if (canAccessStep(pathname, onboardingPhase)) return

    replace(firstStepUrlInOnboardingPhase(onboardingPhase, candidateSlug))
  }, [isLoggedIn, onboardingPhase, confirmingSignupDetails, candidateSlug, pathname, replace])

  const completedOnboarding = isLoggedIn && props.candidate.current_state !== 'onboarding'
  if (completedOnboarding) return <LoadingScene />

  return (
    <div>
      <Choose>
        <When condition={!isLoggedIn || confirmingSignupDetails}>
          <StandardNavigation showNavLinks={!confirmingSignupDetails} />
        </When>
        <Otherwise>
          <Header />
          <ProgressBar progress={props.progress} />
        </Otherwise>
      </Choose>

      <div className={styles.container}>
        <Switch>
          <Route exact path="/candidate/signup" render={() => <CreateUser refetchCurrentUser={props.refetchCurrentUser} />} />
          <If condition={!isLoggedIn}>
            <Redirect to="/candidate/signup" />
          </If>

          {/* START temporary redirects: remove after 01/09/2022 */}
          <Redirect from="/candidate/confirm-details" to={`/candidate/${candidateSlug}/confirm-details`} />
          <Redirect from="/candidate/resume" to={`/candidate/${candidateSlug}/resume`} />
          <Redirect from="/candidate/area-roles" to={`/candidate/${candidateSlug}/area-roles`} />
          <Redirect from="/candidate/skills" to={`/candidate/${candidateSlug}/skills`} />
          <Redirect from="/candidate/employment-type" to={`/candidate/${candidateSlug}/employment-type`} />
          <Redirect from="/candidate/city" to={`/candidate/${candidateSlug}/city`} />
          <Redirect from="/candidate/job-search" to={`/candidate/${candidateSlug}/job-search`} />
          <Redirect from="/candidate/citizenship-status" to={`/candidate/${candidateSlug}/citizenship-status`} />
          <Redirect from="/candidate/salary-expectations" to={`/candidate/${candidateSlug}/salary-expectations`} />
          <Redirect from="/candidate/contact-number" to={`/candidate/${candidateSlug}/contact-number`} />
          <Redirect from="/candidate/work-experience" to={`/candidate/${candidateSlug}/work-experience`} />
          <Redirect from="/candidate/education" to={`/candidate/${candidateSlug}/education`} />
          <Redirect from="/candidate/wrapping-up" to={`/candidate/${candidateSlug}/wrapping-up`} />
          {/* END temporary redirects */}

          <Route exact path="/candidate/:candidateSlug/confirm-details" component={SocialSignupConfirm} />

          <Route exact path="/candidate/:candidateSlug/resume" component={ResumeScene} />

          <Route exact path="/candidate/:candidateSlug/area-roles" component={AreaRolesScene} />
          <Route exact path="/candidate/:candidateSlug/skills" component={SkillsScene} />
          <Route exact path="/candidate/:candidateSlug/employment-type" component={EmploymentType} />
          <Route exact path="/candidate/:candidateSlug/city" component={CitySelect} />

          <Route exact path="/candidate/:candidateSlug/job-search" component={JobSearchStatus} />
          <Route exact path="/candidate/:candidateSlug/citizenship-status" component={CitizenshipStatusScene} />
          <Route exact path="/candidate/:candidateSlug/salary-expectations" component={SalaryExpectations} />
          <Route exact path="/candidate/:candidateSlug/contact-number" component={CellphoneNumber} />

          <Route exact path="/candidate/:candidateSlug/work-experience" component={WorkExperienceScene} />
          <Route exact path="/candidate/:candidateSlug/education" component={EducationScene} />

          <Route exact path="/candidate/:candidateSlug/wrapping-up" component={WrappingUp} />
        </Switch>
      </div>
    </div>
  )
}

OnboardingMain.propTypes = {
  progress: PropTypes.number,
  candidate: PropTypes.shape({
    slug: PropTypes.string,
    onboarding_phase: PropTypes.string,
    current_state: PropTypes.string,
  }),
  onboardingPhase: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }),
  refetchCurrentUser: PropTypes.func,
}

const mapStateToProps = state => ({
  progress: state.utility.progress,
  candidate: state.candidate,
  onboardingPhase: getOnboardingPhase(state),
})

export default withRouter(connect(mapStateToProps)(OnboardingMain))
